import React, { useState } from 'react';
import Loading from '../components/Loading';
import { getRemediesReading, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';
import { ZODIAC_SIGNS } from '../data/astroData';

const remedyCategories = [
  { id: 'gemstones', icon: '💎', key: 'remedyGemstones' },
  { id: 'mantras', icon: '🕉️', key: 'remedyMantras' },
  { id: 'fasting', icon: '🍽️', key: 'remedyFasting' },
  { id: 'charity', icon: '🙏', key: 'remedyCharity' },
  { id: 'yantra', icon: '📿', key: 'remedyYantra' },
  { id: 'puja', icon: '🪔', key: 'remedyPuja' },
];

const doshaTypes = [
  { id: 'manglik', name: 'Manglik Dosha', icon: '♂️' },
  { id: 'kalsarpa', name: 'Kalsarpa Dosha', icon: '🐍' },
  { id: 'shani', name: 'Shani Dosha', icon: '🪐' },
  { id: 'rahu', name: 'Rahu Dosha', icon: '☊' },
  { id: 'ketu', name: 'Ketu Dosha', icon: '☋' },
  { id: 'pitru', name: 'Pitru Dosha', icon: '👴' },
];

export default function Remedies() {
  const { language, t } = useLanguage();
  const [selectedSign, setSelectedSign] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDosha, setSelectedDosha] = useState('');
  const [concern, setConcern] = useState('');
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGetRemedies = async () => {
    if (!selectedSign && !selectedDosha && !concern) {
      setError(t('selectSignOrDosha'));
      return;
    }
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }
    setLoading(true);
    setError('');
    setReading('');
    try {
      const result = await getRemediesReading(
        selectedSign,
        selectedCategory,
        selectedDosha,
        concern,
        language
      );
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to get remedies.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('remediesTitle')}</h2>
          <p>{t('remediesDesc')}</p>
        </div>

        {/* Remedy Categories */}
        <div className="remedy-categories">
          <h3>{t('remedyTypes')}</h3>
          <div className="category-grid">
            {remedyCategories.map(cat => (
              <button
                key={cat.id}
                className={`category-card ${selectedCategory === cat.id ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
              >
                <span className="category-icon">{cat.icon}</span>
                <span className="category-name">{t(cat.key)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Zodiac Selection */}
        <div className="form-section">
          <h3>{t('selectYourSign')}</h3>
          <div className="zodiac-mini-grid">
            {ZODIAC_SIGNS.map(sign => (
              <button
                key={sign.name}
                className={`zodiac-mini-card ${selectedSign === sign.name ? 'selected' : ''}`}
                onClick={() => setSelectedSign(selectedSign === sign.name ? '' : sign.name)}
              >
                <span className="sign-symbol">{sign.symbol}</span>
                <span className="sign-name">{sign.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Dosha Selection */}
        <div className="form-section">
          <h3>{t('orSelectDosha')}</h3>
          <div className="dosha-grid">
            {doshaTypes.map(dosha => (
              <button
                key={dosha.id}
                className={`dosha-card ${selectedDosha === dosha.id ? 'selected' : ''}`}
                onClick={() => setSelectedDosha(selectedDosha === dosha.id ? '' : dosha.id)}
              >
                <span className="dosha-icon">{dosha.icon}</span>
                <span className="dosha-name">{dosha.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Specific Concern */}
        <div className="form-section">
          <div className="form-group">
            <label>{t('specificConcern')}</label>
            <textarea
              value={concern}
              onChange={(e) => setConcern(e.target.value)}
              placeholder={t('describeConcern')}
              rows={3}
            />
          </div>
        </div>

        {/* Get Remedies Button */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button className="btn btn-primary" onClick={handleGetRemedies} disabled={loading}>
            {loading ? t('findingRemedies') : t('getRemedies')}
          </button>
        </div>

        {loading && <Loading message={t('loadingRemedies')} />}
        {error && <div className="error-message">{error}</div>}

        {reading && (
          <div className="result-container">
            <div className="result-card">
              <h3>🙏 {t('remediesResult')}</h3>
              <div className="content">{reading}</div>
            </div>
          </div>
        )}

        {/* Quick Remedy Reference */}
        <div className="quick-reference">
          <h3>{t('quickRemedyGuide')}</h3>
          <div className="reference-grid">
            <div className="reference-card">
              <h4>🌞 {t('sunday')}</h4>
              <p>{t('sundayRemedy')}</p>
            </div>
            <div className="reference-card">
              <h4>🌙 {t('monday')}</h4>
              <p>{t('mondayRemedy')}</p>
            </div>
            <div className="reference-card">
              <h4>♂️ {t('tuesday')}</h4>
              <p>{t('tuesdayRemedy')}</p>
            </div>
            <div className="reference-card">
              <h4>☿ {t('wednesday')}</h4>
              <p>{t('wednesdayRemedy')}</p>
            </div>
            <div className="reference-card">
              <h4>♃ {t('thursday')}</h4>
              <p>{t('thursdayRemedy')}</p>
            </div>
            <div className="reference-card">
              <h4>♀ {t('friday')}</h4>
              <p>{t('fridayRemedy')}</p>
            </div>
            <div className="reference-card">
              <h4>♄ {t('saturday')}</h4>
              <p>{t('saturdayRemedy')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
